'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import UserName from '@/components/UserName'
import WarningBlocks from '@/components/WarningBlocks'
import ReputationBadge from '@/components/ReputationBadge'
import QuoteButton from '@/components/QuoteButton'
import DeletePostButton from '@/components/DeletePostButton'
import VoteButtons from '@/components/VoteButtons'
import ReportModal from '@/components/ReportModal'
import MermaidDiagram from '@/components/MermaidDiagram' // <-- Imported the new component
import { timeAgo } from '@/lib/timeAgo'
import { getUserRank } from '@/lib/ranks'
import { toast } from 'sonner'
import { motion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { Smartphone } from 'lucide-react'
import rehypeSanitize from 'rehype-sanitize'

export default function ThreadPostsFeed({
  threadId,
  initialPosts,
  totalPostsCount,
  currentUserId,
  userRole,
  threadTags,
  threadAuthorId,
  allowMarkAsSolved,
  isThreadSolved,
  solvedPostId
}: {
  threadId: string
  initialPosts: any[]
  totalPostsCount: number
  currentUserId: string | null
  userRole: string | undefined
  threadTags: string[]
  threadAuthorId: string
  allowMarkAsSolved: boolean
  isThreadSolved: boolean
  solvedPostId: string | null
}) {
  const [posts, setPosts] = useState(initialPosts)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [reportingPostId, setReportingPostId] = useState<string | null>(null)

  useEffect(() => {
    setPosts(initialPosts)
  }, [initialPosts])

  useEffect(() => {
    const handleNewPost = (e: any) => {
      const { post } = e.detail;
      if (!post) return;

      setPosts((prevPosts) => [...prevPosts, post]);
      setTimeout(() => {
        const postElement = document.getElementById(`post-${post.id}`);
        if (postElement) {
          postElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          postElement.classList.add('bg-indigo-900/40', 'transition-colors', 'duration-1000');
          setTimeout(() => postElement.classList.remove('bg-indigo-900/40'), 2000);
        }
      }, 100);
    };

    window.addEventListener('post-created', handleNewPost);
    return () => window.removeEventListener('post-created', handleNewPost);
  }, []);

  const POSTS_PER_PAGE = 20
  const hasMore = posts.length < totalPostsCount

  const loadMorePosts = async () => {
    if (loading || !hasMore) return
    setLoading(true)
    try {
      const nextPage = page + 1
      const res = await fetch(`/api/threads/${threadId}/posts?page=${nextPage}&limit=${POSTS_PER_PAGE}`)
      if (!res.ok) throw new Error('Failed to load more posts')
      const data = await res.json()
      setPosts(prev => [...prev, ...data.posts])
      setPage(nextPage)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="bg-[#070b14]/90 rounded-2xl border border-slate-800/80 shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden backdrop-blur-xl">
        <div className="flex flex-col">
          {posts.map((post, index) => {
            const absoluteIndex = index
            const score = post.votes ? post.votes.reduce((sum: number, v: any) => sum + v.value, 0) : 0
            const userVoteRecord = post.votes ? post.votes.find((v: any) => v.userId === currentUserId) : undefined
            const userInitialVote = userVoteRecord ? userVoteRecord.value : undefined
            const isSolution = solvedPostId === post.id
            const userPoints = post.author?.transactions?.reduce((sum: number, t: any) => sum + t.amount, 0) || 0
            const rank = getUserRank(userPoints)
            const reputation = post.author?.reputation !== undefined ? post.author.reputation : 0
            const canSolve = allowMarkAsSolved && !isThreadSolved && (currentUserId === threadAuthorId || userRole === 'ADMIN' || userRole === 'MODERATOR')
            const isLast = index === posts.length - 1

            return (
              <motion.div 
                key={post.id}
                id={`post-${post.id}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: index * 0.05 }}
                className={`flex gap-3 md:gap-4 group relative px-4 md:px-8 py-6 transition-all duration-200 ${
                  isSolution 
                    ? 'bg-gradient-to-r from-emerald-950/30 to-[#070b14] border-l-4 border-l-emerald-500 border-b border-b-slate-800/50' 
                    : 'hover:bg-slate-900/40 border-l-4 border-l-transparent border-b border-b-slate-800/50 last:border-b-0'
                }`}
              >
                
                <div className="flex flex-col items-center shrink-0 w-12 mt-1">
                  <div className="relative group/avatar cursor-pointer">
                    <div className={`absolute inset-0 rounded-xl blur-md opacity-40 transition-opacity group-hover/avatar:opacity-80 ${
                      post.author?.role === 'ADMIN' ? 'bg-rose-500' :
                      post.author?.role === 'MODERATOR' ? 'bg-emerald-500' :
                      'bg-indigo-500'
                    }`} />
                    <div className={`relative w-11 h-11 rounded-xl p-[2px] shadow-lg ${
                      post.author?.role === 'ADMIN' ? 'bg-gradient-to-br from-rose-500 to-rose-900' :
                      post.author?.role === 'MODERATOR' ? 'bg-gradient-to-br from-emerald-500 to-emerald-900' :
                      'bg-gradient-to-br from-indigo-500 to-slate-900'
                    }`}>
                      {post.author?.image ? (
                        <img src={post.author.image} alt="" className="w-full h-full rounded-[10px] object-cover bg-slate-950" />
                      ) : (
                        <div className="w-full h-full rounded-[10px] bg-slate-900 flex items-center justify-center text-sm font-black text-slate-300">
                          {post.author?.name?.charAt(0).toUpperCase() || '?'}
                        </div>
                      )}
                    </div>
                  </div>
                  {!isLast && <div className={`flex-1 w-[2px] mt-3 ${isSolution ? 'bg-emerald-500/40' : 'bg-slate-800/80 group-hover:bg-slate-700'}`} />}
                </div>

                <div className="flex-1 min-w-0 pb-2">
                  <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 mb-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className={post.author?.status === 'BANNED' ? 'line-through text-slate-500' : ''}>
                        <UserName 
                          userId={post.author?.id || 'unknown'} 
                          name={post.author?.name || 'Deleted User'} 
                          cosmetics={post.author?.cosmetics}
                          className="font-bold text-white hover:text-indigo-300 text-[15px]" 
                        />
                      </div>

                      {post.author?.badges && post.author.badges.length > 0 && (
                        <div className="flex items-center -space-x-1 ml-1">
                          {post.author.badges.slice(0, 4).map((userBadge: any) => (
                            <div key={userBadge.badge.id} className="relative group/badge cursor-pointer z-10 hover:z-20">
                              <div className="w-5 h-5 flex items-center justify-center bg-slate-900 border border-slate-700 hover:border-indigo-400 rounded-md text-[10px] transition-all shadow-md">
                                {userBadge.badge.icon}
                              </div>
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 opacity-0 invisible group-hover/badge:opacity-100 group-hover/badge:visible transition-all duration-200 z-30 pointer-events-none">
                                <div className="bg-slate-800 border border-slate-700 rounded-lg p-2 text-center shadow-2xl">
                                  <p className="text-xs font-bold text-white mb-0.5">{userBadge.badge.name}</p>
                                  <p className="text-[10px] text-slate-400 leading-tight">{userBadge.badge.description}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <span className="font-mono text-slate-500 text-xs ml-1">@{post.author?.username || 'user'}</span>

                      {post.isMobile && (
                        <span className="inline-flex items-center text-indigo-400 bg-indigo-950/50 px-1.5 py-0.5 rounded border border-indigo-500/20 shadow-sm">
                          <Smartphone className="w-3 h-3" />
                        </span>
                      )}

                      <span className="text-slate-700 mx-1">&bull;</span>
                      
                      <div className="flex items-center gap-2 font-mono text-[10px] bg-slate-900/50 px-2 py-1 rounded-md border border-slate-800/80">
                        {post.author?.role && post.author.role !== 'USER' && (
                          <>
                            <span className={`font-bold ${post.author.role === 'ADMIN' ? 'text-rose-400' : 'text-emerald-400'}`}>
                              ${post.author.role.toLowerCase()}
                            </span>
                            <span className="text-slate-700">|</span>
                          </>
                        )}
                        <span className="text-indigo-400 font-bold">~/{rank.title.toLowerCase().replace(/\s+/g, '-')}</span>
                        <span className="text-slate-700">|</span>
                        <span className="scale-[0.85] origin-left inline-flex"><ReputationBadge targetUserId={post.author.id} initialRep={reputation} currentUserId={currentUserId} /></span>
                        {post.author?.warningCount ? (
                          <>
                            <span className="text-slate-700 ml-1">|</span>
                            <span className="scale-[0.75] origin-left inline-flex ml-1"><WarningBlocks count={post.author.warningCount} /></span>
                          </>
                        ) : null}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 pt-1">
                      <span className="text-[10px] font-mono text-slate-500 hover:text-slate-300 transition-colors cursor-pointer">{timeAgo(post.createdAt)}</span>
                      <span className="text-[10px] font-mono text-slate-700 font-bold">#{absoluteIndex + 1}</span>
                    </div>
                  </div> 

                  <div className="text-slate-200 leading-relaxed text-[15px] break-words prose prose-invert max-w-none">
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw, rehypeSanitize]}
                      components={{
                        blockquote: ({ children, ...props }: any) => (
                          <blockquote className="border-l-4 border-indigo-500 bg-indigo-950/20 p-4 rounded-r-lg italic text-slate-300" {...props}>
                            {children}
                          </blockquote>
                        ),
                        // 1. Intercept the outer wrapper
                        pre: ({ children }: any) => {
                          const className = children?.props?.className || ''
                          const match = /language-(\w+)/.exec(className)
                          
                          // If it is mermaid, bypass the code wrapper UI entirely
                          if (match && match[1] === 'mermaid') {
                            return <>{children}</> 
                          }
                          
                          return (
                            <div className="not-prose bg-[#020408] border border-slate-800 rounded-xl my-4 overflow-hidden shadow-2xl">
                              <div className="bg-[#070b14] border-b border-slate-800 px-4 py-2.5 flex items-center justify-between">
                                <span className="text-[10px] font-mono font-bold text-indigo-400 uppercase tracking-widest flex items-center gap-2">
                                  <span>~/</span> {match ? match[1] : 'code'}
                                </span>
                              </div>
                              <div className="p-4 overflow-x-auto text-sm text-slate-300 font-mono whitespace-pre">
                                {children}
                              </div>
                            </div>
                          )
                        },
                        // 2. Intercept the actual code execution
                        code: ({ inline, className, children, ...props }: any) => {
                          const match = /language-(\w+)/.exec(className || '')
                          
                          // Render the diagram component
                          if (!inline && match && match[1] === 'mermaid') {
                            return <MermaidDiagram chart={String(children).replace(/\n$/, '')} />
                          }
                          
                          return inline 
                            ? <code className="bg-[#020408] text-indigo-300 px-1.5 py-0.5 rounded text-[12px] font-mono border border-slate-800/80" {...props}>{children}</code>
                            : <code className={className} {...props}>{children}</code>
                        }
                      }}
                    >
                      {post.content}
                    </ReactMarkdown>
                  </div>

                  {absoluteIndex === 0 && threadTags && threadTags.length > 0 && (
                    <div className="mt-4 flex items-center flex-wrap gap-2">
                      {threadTags.map(tag => (
                        <Link key={tag} href={`/?tag=${encodeURIComponent(tag)}`} className="text-[10px] text-indigo-400 font-mono font-bold bg-indigo-950/40 border border-indigo-500/20 hover:border-indigo-500/40 transition-colors px-2 py-1 rounded-md shadow-sm">
                          #{tag}
                        </Link>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-4 mt-5 -ml-2">
                    <div className="opacity-80 hover:opacity-100 transition-opacity"><VoteButtons postId={post.id} initialScore={score} userInitialVote={userInitialVote} /></div>
                    <div className="opacity-80 hover:opacity-100 transition-opacity"><QuoteButton content={post.content} authorName={post.author?.name || 'Unknown'} authorId={post.author?.id || ''} /></div>
                    
                    {currentUserId && currentUserId !== post.author?.id && (
                      <button onClick={() => setReportingPostId(post.id)} className="opacity-80 hover:opacity-100 text-slate-500 hover:text-rose-400 transition-colors flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider font-mono">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" /></svg>
                        <span>Report</span>
                      </button>
                    )}

                    {(userRole === 'ADMIN' || userRole === 'MODERATOR') && (
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity"><DeletePostButton postId={post.id} /></div>
                    )}

                    {canSolve && (
                      <button onClick={() => toast.promise(fetch(`/api/posts/${post.id}/solve`, { method: 'POST' }).then(res => res.json()), { loading: 'Accepting solution...', success: () => { setTimeout(() => window.location.reload(), 1500); return 'Solution accepted!' }, error: 'Failed' })} className="ml-auto flex items-center gap-1.5 text-[11px] font-bold text-slate-500 hover:text-emerald-400 transition-colors font-mono bg-slate-900/50 hover:bg-emerald-950/30 px-3 py-1.5 rounded-lg border border-slate-800 hover:border-emerald-500/30 shadow-sm">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                        Accept
                      </button>
                    )}

                    {isSolution && (
                      <span className="ml-auto flex items-center gap-1.5 text-[11px] font-mono font-black uppercase tracking-widest text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-3 py-1.5 rounded-lg shadow-[0_0_15px_rgba(52,211,153,0.1)]">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                        Solution
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>

        {totalPostsCount > POSTS_PER_PAGE && (
          <div className="bg-[#020408] border-t border-slate-800/80 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-xs text-slate-500 font-mono">
              Viewing <span className="text-indigo-400 font-bold">{posts.length}</span> / {totalPostsCount} posts
            </div>
            {hasMore && (
              <button onClick={loadMorePosts} disabled={loading} className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold px-6 py-2.5 rounded-lg text-xs transition-all shadow-[0_0_15px_rgba(99,102,241,0.3)] disabled:opacity-50 font-mono">
                {loading ? 'Fetching...' : 'Load more replies ↓'}
              </button>
            )}
          </div>
        )}
      </div>
      <ReportModal isOpen={!!reportingPostId} onClose={() => setReportingPostId(null)} targetId={reportingPostId || ''} targetType="POST" />
    </>
  )
}